// Epoch 19: Lead Generator Enhancements - Base Pricing Editor Component
// File: src/components/admin/BasePricingEditor.tsx
// Purpose: Editor for base pricing configuration (price per 1000 leads, max leads, etc.)

import React from 'react';

interface BasePricingData {
  base_price_per_1000: number;
  max_leads_per_export: number;
  currency: string;
}

interface BasePricingEditorProps {
  basePricing: BasePricingData;
  onChange: (basePricing: BasePricingData) => void;
}

export default function BasePricingEditor({ basePricing, onChange }: BasePricingEditorProps) {
  const handleChange = (field: keyof BasePricingData, value: string | number) => {
    onChange({
      ...basePricing,
      [field]: value
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: basePricing.currency || 'USD'
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Base Pricing Configuration</h3>
        <p className="text-sm text-gray-600">
          Configure the fundamental pricing structure for lead exports.
        </p>
      </div>

      {/* Configuration Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Base Price Per 1000 Leads */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Base Price per 1,000 Leads
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 text-sm">$</span>
            </div>
            <input
              type="number"
              value={basePricing.base_price_per_1000}
              onChange={(e) => handleChange('base_price_per_1000', parseFloat(e.target.value) || 0)}
              className="block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="50.00"
              step="0.01"
              min="0"
            />
          </div>
          <p className="text-xs text-gray-500">
            Base cost before tier upcharges are applied
          </p>
        </div>

        {/* Maximum Leads Per Export */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Max Leads per Export
          </label>
          <input
            type="number"
            value={basePricing.max_leads_per_export}
            onChange={(e) => handleChange('max_leads_per_export', parseInt(e.target.value) || 0)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="1000"
            min="1"
            max="10000"
          />
          <p className="text-xs text-gray-500">
            Maximum number of leads allowed in a single export
          </p>
        </div>

        {/* Currency */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Currency
          </label>
          <select
            value={basePricing.currency}
            onChange={(e) => handleChange('currency', e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="USD">USD - US Dollar</option>
            <option value="EUR">EUR - Euro</option>
            <option value="GBP">GBP - British Pound</option>
            <option value="CAD">CAD - Canadian Dollar</option>
          </select>
          <p className="text-xs text-gray-500">
            Currency for all pricing displays and transactions
          </p>
        </div>
      </div>

      {/* Pricing Preview */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-3">Pricing Preview</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-blue-700 font-medium">100 Leads</div>
            <div className="text-blue-600">
              {formatCurrency((basePricing.base_price_per_1000 / 1000) * 100)}
            </div>
          </div>
          <div>
            <div className="text-blue-700 font-medium">500 Leads</div>
            <div className="text-blue-600">
              {formatCurrency((basePricing.base_price_per_1000 / 1000) * 500)}
            </div>
          </div>
          <div>
            <div className="text-blue-700 font-medium">1,000 Leads</div>
            <div className="text-blue-600">
              {formatCurrency(basePricing.base_price_per_1000)}
            </div>
          </div>
        </div>
        <p className="text-xs text-blue-600 mt-2">
          * Base prices only - tier upcharges will be added based on lead quality distribution
        </p>
      </div>

      {/* Validation Warnings */}
      <div className="space-y-2">
        {basePricing.base_price_per_1000 <= 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-700 text-sm">Base price must be greater than $0.00</span>
            </div>
          </div>
        )}

        {basePricing.max_leads_per_export <= 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-700 text-sm">Maximum leads per export must be at least 1</span>
            </div>
          </div>
        )}

        {basePricing.max_leads_per_export > 10000 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span className="text-yellow-700 text-sm">
                High export limits may impact system performance and BigQuery costs
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Best Practices */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">💡 Best Practices</h4>
        <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside">
          <li>Set base price to cover your data acquisition and processing costs</li>
          <li>Consider market rates when setting pricing ($30-$100 per 1K is typical)</li>
          <li>Export limits help manage BigQuery costs and API performance</li>
          <li>Test pricing changes with small user groups before full rollout</li>
          <li>Monitor conversion rates after pricing adjustments</li>
        </ul>
      </div>
    </div>
  );
}